import JSZip from "jszip";

/**
 * Sanitize a file name to be safe across Windows/macOS/Linux file systems.
 */
export const sanitizeFileName = (name: string): string =>
  name.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").replace(/\.+$/, "").trim() || "archivo";

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  // Run entirely outside React's lifecycle to avoid reconciliation issues
  // (see memory: error-handling-strategy / dom-stabilization-production).
  window.setTimeout(() => {
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.rel = "noopener";
      a.click();
      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      }, 120000);
    } catch (e) {
      if (typeof console !== "undefined") console.error("Download error:", e);
    }
  }, 0);
};

/**
 * Pack a single file into a .zip and trigger its download.
 *
 * Why ZIP for a single file: when a PDF is downloaded directly from the browser,
 * Windows attaches the "Mark of the Web" attribute, which causes Acrobat / the
 * Explorer preview pane to block the file as "untrusted" and force-open it.
 * Files extracted from a ZIP do not inherit MOTW, so the preview works normally.
 *
 * @param file     The original File/Blob to pack.
 * @param baseName Logical name for the file inside the ZIP (extension included or implied).
 *                 The ZIP itself is named `<baseName-without-extension>.zip`.
 */
export const downloadFileAsZip = async (file: File | Blob, baseName: string): Promise<void> => {
  const safeInner = sanitizeFileName(baseName);
  // Strip extension for the outer .zip name; keep inner file with its real name.
  const lastDot = safeInner.lastIndexOf(".");
  const zipBase = lastDot > 0 ? safeInner.slice(0, lastDot) : safeInner;
  const zipName = `${zipBase}.zip`;

  try {
    const buffer = await file.arrayBuffer();
    const zip = new JSZip();
    zip.file(safeInner, buffer);
    const blob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
    triggerBlobDownload(blob, zipName);
  } catch (e) {
    if (typeof console !== "undefined") console.error("Single-file ZIP error:", e);
  }
};

/**
 * Pack multiple files into a single .zip and download it.
 */
export const downloadFilesAsZip = async (
  items: Array<{ file: File | Blob; name: string }>,
  zipName: string,
): Promise<void> => {
  if (items.length === 0) return;
  try {
    const zip = new JSZip();
    for (const item of items) {
      try {
        const buffer = await item.file.arrayBuffer();
        zip.file(sanitizeFileName(item.name), buffer);
      } catch {
        /* skip corrupt file */
      }
    }
    const blob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
    triggerBlobDownload(blob, sanitizeFileName(zipName));
  } catch (e) {
    if (typeof console !== "undefined") console.error("Multi-file ZIP error:", e);
  }
};
